import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import HelpSection from './HelpSection';

export default function AdvisorSettingsEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [draft, setDraft] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config) {
      setDraft({
        hoursScenarios: config.advisorSettings?.hoursScenarios || [20, 25, 30],
        waitingPeriodScenarios: config.advisorSettings?.waitingPeriodScenarios || [0, 30, 60, 90],
        nearEligibleSalaryBuffer: config.advisorSettings?.nearEligibleSalaryBuffer || 3000,
        hceWarningThreshold: config.advisorSettings?.hceWarningThreshold || 20,
        highImpactEmployeeThreshold: config.advisorSettings?.highImpactEmployeeThreshold || 5,
        highImpactSavingsThreshold: config.advisorSettings?.highImpactSavingsThreshold || 5000,
        mediumImpactEmployeeThreshold: config.advisorSettings?.mediumImpactEmployeeThreshold || 2,
        mediumImpactSavingsThreshold: config.advisorSettings?.mediumImpactSavingsThreshold || 2000,
        showInPDF: config.advisorSettings?.showInPDF !== false,
        showNonDiscrimination: config.advisorSettings?.showNonDiscrimination !== false,
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...draft[field]];
    updated[index] = parseFloat(value) || 0;
    setDraft(prev => ({ ...prev, [field]: updated }));
    setHasChanges(true);
  };

  const addToArray = (field) => {
    setDraft(prev => ({ ...prev, [field]: [...prev[field], 0] }));
    setHasChanges(true);
  };

  const removeFromArray = (field, index) => {
    setDraft(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
    setHasChanges(true);
  };

  const save = async () => {
    try {
      await updateConfig({
        advisorSettings: draft
      });
      setHasChanges(false);
      showToast('Advisor settings saved successfully', 'success');
    } catch (err) {
      showToast('Failed to save advisor settings', 'error');
    }
  };

  const cancel = () => {
    setDraft({
      hoursScenarios: config.advisorSettings?.hoursScenarios || [20, 25, 30],
      waitingPeriodScenarios: config.advisorSettings?.waitingPeriodScenarios || [0, 30, 60, 90],
      nearEligibleSalaryBuffer: config.advisorSettings?.nearEligibleSalaryBuffer || 3000,
      hceWarningThreshold: config.advisorSettings?.hceWarningThreshold || 20,
      highImpactEmployeeThreshold: config.advisorSettings?.highImpactEmployeeThreshold || 5,
      highImpactSavingsThreshold: config.advisorSettings?.highImpactSavingsThreshold || 5000,
      mediumImpactEmployeeThreshold: config.advisorSettings?.mediumImpactEmployeeThreshold || 2,
      mediumImpactSavingsThreshold: config.advisorSettings?.mediumImpactSavingsThreshold || 2000,
      showInPDF: config.advisorSettings?.showInPDF !== false,
      showNonDiscrimination: config.advisorSettings?.showNonDiscrimination !== false,
    });
    setHasChanges(false);
  };

  if (!draft) return <div>Loading...</div>;

  const helpSections = [
    {
      heading: 'What is the Eligibility Advisor?',
      content: 'A recommendation engine that analyzes an employer\'s workforce and suggests eligibility rule changes (hours thresholds, waiting periods) to maximize enrollment while maintaining compliance. It runs automatically on the Workforce tab and shows actionable recommendations ranked by impact.'
    },
    {
      heading: 'Scenario Thresholds',
      content: 'Pre-defined comparison points shown in recommendations:',
      impacts: [
        'Hours Scenarios: When suggesting "Adjust Hours Threshold," the advisor shows projected impact at 20, 25, and 30 hours/week',
        'Waiting Period Scenarios: When suggesting "Reduce Waiting Period," shows 0, 30, 60, and 90 day options',
        'These are NOT applied automatically — they are what-if scenarios for the employer to review'
      ]
    },
    {
      heading: 'Detection Thresholds',
      content: 'Rules that trigger advisor recommendations:',
      impacts: [
        'Near-Eligible Salary Buffer: Employees within $3,000 of break-even are flagged as "almost eligible" → triggers salary adjustment recommendation',
        'HCE Warning Threshold: If 20%+ of benefits go to Highly Compensated Employees, show "At Risk" warning (IRS limit is 25%)'
      ]
    },
    {
      heading: 'Impact Badges (High/Medium/Low)',
      content: 'Visual priority ranking for recommendations:',
      impacts: [
        'High Impact: ≥5 additional employees OR ≥$5,000 annual employer savings',
        'Medium Impact: ≥2 employees OR ≥$2,000 savings',
        'Low Impact: Below medium thresholds — informational only'
      ]
    },
    {
      heading: 'Non-Discrimination Testing',
      content: 'Simplified IRC §125 compliance check comparing Highly Compensated Employees (HCE) to Non-HCEs:',
      impacts: [
        'HCE Salary Threshold: Currently $155,000 (IRS-indexed annually)',
        'Key Employee rules: Ownership % threshold for stricter limits',
        'Benefit Concentration: Max % of total benefits that can go to Key Employees (IRS limit: 25%)'
      ]
    },
    {
      heading: 'Where this appears',
      content: (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>Workforce tab: "Eligibility Advisor" panel with ranked recommendations</li>
          <li>PDF reports: Optional inclusion of top 3 recommendations (toggle below)</li>
          <li>Employer Portal: Compliance warnings if thresholds exceeded</li>
        </ul>
      )
    }
  ];

  return (
    <div style={S.card}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>Advisor Settings</h2>
      <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>Configure thresholds and scenarios used by the Eligibility Advisor when generating recommendations.</p>

      <HelpSection title="How does the Eligibility Advisor work?" sections={helpSections} />

      {/* Scenario Thresholds */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Scenario Thresholds</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Hours Threshold Scenarios</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Comparison points shown in "Adjust Hours" recommendation</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {draft.hoursScenarios.map((hours, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input 
                  type="number"
                  value={hours}
                  onChange={e => handleArrayChange('hoursScenarios', idx, e.target.value)}
                  style={{ ...S.input, width: 70, padding: '6px 8px' }}
                  min="0"
                />
                <button
                  onClick={() => removeFromArray('hoursScenarios', idx)}
                  style={{ background: 'transparent', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16 }}
                >
                  ×
                </button>
              </div>
            ))}
            <button onClick={() => addToArray('hoursScenarios')} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>➕ Add</button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Waiting Period Scenarios</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Days shown in "Reduce Waiting Period" recommendation</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {draft.waitingPeriodScenarios.map((days, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input 
                  type="number"
                  value={days}
                  onChange={e => handleArrayChange('waitingPeriodScenarios', idx, e.target.value)}
                  style={{ ...S.input, width: 70, padding: '6px 8px' }}
                  min="0"
                />
                <button
                  onClick={() => removeFromArray('waitingPeriodScenarios', idx)}
                  style={{ background: 'transparent', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16 }}
                >
                  ×
                </button>
              </div>
            ))}
            <button onClick={() => addToArray('waitingPeriodScenarios')} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>➕ Add</button>
          </div>
        </div>
      </div>

      {/* Detection Thresholds */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Detection Thresholds</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Near-Eligible Salary Buffer</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Employees within this amount of break-even salary are flagged</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
            <input 
              type="number"
              value={draft.nearEligibleSalaryBuffer}
              onChange={e => handleChange('nearEligibleSalaryBuffer', parseFloat(e.target.value) || 0)}
              style={{ ...S.input, width: 120 }}
              min="0"
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>HCE Test Warning Threshold</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Show "At Risk" when HCE benefits exceed this percentage (test fails at 25%)</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="number"
              value={draft.hceWarningThreshold}
              onChange={e => handleChange('hceWarningThreshold', parseFloat(e.target.value) || 0)}
              style={{ ...S.input, width: 100 }}
              min="0"
              max="25"
            />
            <span style={{ fontSize: 14, fontWeight: 600 }}>%</span>
          </div>
        </div>
      </div>

      {/* Recommendation Display */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Recommendation Display</h3>
        
        <label style={S.label}>Impact Thresholds (for High/Medium/Low badges)</label>
        <p style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>Define what qualifies as high/medium impact</p>
        
        <div style={{ padding: 12, background: C.bg, borderRadius: 8, marginBottom: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: C.textMid }}>High Impact:</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13 }}>≥</span>
                <input 
                  type="number"
                  value={draft.highImpactEmployeeThreshold}
                  onChange={e => handleChange('highImpactEmployeeThreshold', parseFloat(e.target.value) || 0)}
                  style={{ ...S.input, width: 80, padding: '6px 8px' }}
                  min="0"
                />
                <span style={{ fontSize: 13, color: C.textMid }}>additional employees</span>
              </div>
              <span style={{ fontSize: 13, color: C.textMid }}>OR</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13 }}>≥ $</span>
                <input 
                  type="number"
                  value={draft.highImpactSavingsThreshold}
                  onChange={e => handleChange('highImpactSavingsThreshold', parseFloat(e.target.value) || 0)}
                  style={{ ...S.input, width: 100, padding: '6px 8px' }}
                  min="0"
                />
                <span style={{ fontSize: 13, color: C.textMid }}>savings</span>
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: C.textMid }}>Medium Impact:</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13 }}>≥</span>
                <input 
                  type="number"
                  value={draft.mediumImpactEmployeeThreshold}
                  onChange={e => handleChange('mediumImpactEmployeeThreshold', parseFloat(e.target.value) || 0)}
                  style={{ ...S.input, width: 80, padding: '6px 8px' }}
                  min="0"
                />
                <span style={{ fontSize: 13, color: C.textMid }}>employees</span>
              </div>
              <span style={{ fontSize: 13, color: C.textMid }}>OR</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13 }}>≥ $</span>
                <input 
                  type="number"
                  value={draft.mediumImpactSavingsThreshold}
                  onChange={e => handleChange('mediumImpactSavingsThreshold', parseFloat(e.target.value) || 0)}
                  style={{ ...S.input, width: 100, padding: '6px 8px' }}
                  min="0"
                />
                <span style={{ fontSize: 13, color: C.textMid }}>savings</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: 11, color: C.textMid, fontStyle: 'italic' }}>
            Below Medium: Informational
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
            <input 
              type="checkbox"
              checked={draft.showInPDF}
              onChange={e => handleChange('showInPDF', e.target.checked)}
            />
            Include top 3 advisor recommendations in the PDF
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input 
              type="checkbox"
              checked={draft.showNonDiscrimination}
              onChange={e => handleChange('showNonDiscrimination', e.target.checked)}
            />
            Show simplified non-discrimination test status
          </label>
        </div>
      </div>

      {/* Save/Cancel */}
      {hasChanges && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancel} style={S.buttonSecondary}>Cancel</button>
          <button data-testid="save-advisor-settings-btn" onClick={save} style={S.buttonPrimary}>Save Changes</button>
        </div>
      )}
    </div>
  );
}
